import type {
  IApiMediaWithCropsModel,
  WoongebouwDetailPaginaContentResponseModel,
  WoongebouwDetailPaginaPropertiesModel,
} from '@woonstad/api-client-content-delivery';
import type {
  Woonstad_Foundation_Portfolio_Api_Models_Shared_Image,
  Woonstad_Foundation_Portfolio_Api_Models_WRM_StadswonenComplex,
} from '@woonstad/api-client-stadswonen-aanbod';
import { getMediaProperties, stripHTML } from '@woonstad/helpers-utils';

type ComplexPickerValue =
  | number
  | string
  | { id?: string | number; name?: string | null };

function normalizeComplexPickerValues(
  complex?: WoongebouwDetailPaginaPropertiesModel['complex'] | string | null,
): ComplexPickerValue[] {
  if (!complex) {
    return [];
  }

  let values: unknown = complex;

  if (typeof complex === 'string') {
    try {
      values = JSON.parse(complex);
    } catch {
      return [];
    }
  }

  return Array.isArray(values) ? values as ComplexPickerValue[] : [];
}

function parseComplexPickerNumber(value: ComplexPickerValue): number | undefined {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);

    return Number.isNaN(parsed) ? undefined : parsed;
  }

  if (value.id === undefined || value.id === null) {
    return undefined;
  }

  const parsed = Number(value.id);

  return Number.isNaN(parsed) ? undefined : parsed;
}

export function getPrimaryComplexNumber(
  properties?: WoongebouwDetailPaginaPropertiesModel | null,
): number | undefined {
  const [first] = normalizeComplexPickerValues(properties?.complex);

  return first === undefined ? undefined : parseComplexPickerNumber(first);
}

/** WRM slugs often omit the street number that CMS routes include (e.g. bredestraat vs bredestraat-20). */
export function getAlternateWrmUrls(url: string): string[] {
  const withoutStreetNumber = url.replace(/-\d+$/, '');

  return withoutStreetNumber !== url ? [withoutStreetNumber] : [];
}

export function getCmsRouteSlug(path?: string | null): string | undefined {
  return path?.split('/').filter(Boolean).pop();
}

export function replaceLastPathSegment(pathname: string, slug: string): string {
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) {
    return `/${slug}`;
  }

  segments[segments.length - 1] = slug;

  return `/${segments.join('/')}`;
}

export function prefixPathWithLang(lang: string, path: string): string {
  const segments = path.split('/').filter(Boolean);

  if (segments[0] === lang) {
    return `/${segments.join('/')}`;
  }

  return `/${[lang, ...segments].join('/')}`;
}

export function findWrmListUrl(
  items: Array<{ number?: number; url?: string | null }>,
  requestSlug: string,
  complexNumber?: number,
): string | undefined {
  if (complexNumber !== undefined) {
    const byNumber = items.find((item) => item.number === complexNumber)?.url;

    if (byNumber) return byNumber;
  }

  const exact = items.find((item) => item.url === requestSlug)?.url;

  if (exact) return exact;

  const requestAlternates = new Set(getAlternateWrmUrls(requestSlug));
  const byRequestAlternate = items.find((item) => item.url && requestAlternates.has(item.url))?.url;

  if (byRequestAlternate) return byRequestAlternate;

  return items.find((item) => (
    item.url !== undefined
    && item.url !== null
    && getAlternateWrmUrls(item.url).includes(requestSlug)
  ))?.url ?? undefined;
}

/**
 * Canonical CMS slug for a WRM request. Returns undefined when the request
 * already is a CMS slug, or when no woongebouwDetailPagina exists for the
 * complex number.
 */
export function findCmsCanonicalSlug(
  requestSlug: string,
  wrmItems: Array<{ number?: number; url?: string | null }>,
  cmsItems: WoongebouwDetailPaginaContentResponseModel[],
  knownComplexNumber?: number,
): string | undefined {
  const alreadyOnCmsSlug = cmsItems.some(
    (item) => getCmsRouteSlug(item.route?.path) === requestSlug,
  );

  if (alreadyOnCmsSlug) {
    return undefined;
  }

  const wrmListUrl = findWrmListUrl(wrmItems, requestSlug, knownComplexNumber);
  const wrmNumber = wrmItems.find((item) => item.url === wrmListUrl)?.number;
  const complexNumber = knownComplexNumber ?? wrmNumber;

  if (complexNumber === undefined) {
    return undefined;
  }

  const cmsSlug = getCmsRouteSlug(
    buildComplexNumberIndex(cmsItems).get(complexNumber)?.route?.path,
  );

  return cmsSlug && cmsSlug !== requestSlug ? cmsSlug : undefined;
}

/**
 * Prefer a known WRM search slug. Otherwise try street-number-stripped
 * alternates before the request/CMS slug, so we do not wait on WRM 500s
 * for CMS-only paths like `bredestraat-20`.
 */
export function collectWrmUrlCandidates(
  requestSlug: string,
  wrmListUrl?: string,
  cmsSlug?: string,
): string[] {
  if (wrmListUrl) return [wrmListUrl];

  const candidates: string[] = [];

  const add = (url?: string) => {
    if (url && !candidates.includes(url)) candidates.push(url);
  };

  for (const url of getAlternateWrmUrls(requestSlug)) add(url);

  if (cmsSlug) {
    for (const url of getAlternateWrmUrls(cmsSlug)) add(url);
  }

  add(requestSlug);
  if (cmsSlug) add(cmsSlug);

  return candidates;
}

export function buildComplexNumberIndex(
  items: WoongebouwDetailPaginaContentResponseModel[],
): Map<number, WoongebouwDetailPaginaContentResponseModel> {
  const index = new Map<number, WoongebouwDetailPaginaContentResponseModel>();

  for (const item of items) {
    const complexNumber = getPrimaryComplexNumber(item.properties);

    if (complexNumber === undefined) {
      continue;
    }

    index.set(complexNumber, item);
  }

  return index;
}

export function buildWrmComplexUrlIndex(
  items: Array<{ number?: number; url?: string | null }>,
): Map<number, string> {
  const index = new Map<number, string>();

  for (const item of items) {
    if (item.number === undefined || !item.url) {
      continue;
    }

    index.set(item.number, item.url);
  }

  return index;
}

export type WoongebouwCardOverlay = {
  slug?: string;
  title?: string;
  image?: Woonstad_Foundation_Portfolio_Api_Models_Shared_Image;
  description?: string;
};

export function getWoongebouwCardOverlay(
  cmsPage?: WoongebouwDetailPaginaContentResponseModel | null,
): WoongebouwCardOverlay | undefined {
  if (!cmsPage?.properties) {
    return undefined;
  }

  const slug = getCmsRouteSlug(cmsPage.route?.path);

  if (!slug) {
    return undefined;
  }

  const properties = cmsPage.properties;
  const title = properties.titel ?? undefined;
  const image = mapCmsMediaToWrmImage(properties.afbeeldingenCarousel?.[0]);

  const description = properties.introductieTekst
    ? stripHTML(properties.introductieTekst)
    : undefined;

  return {
    slug,
    title,
    image,
    description,
  };
}

export function mapCmsMediaToWrmImage(
  media?: IApiMediaWithCropsModel | null,
): Woonstad_Foundation_Portfolio_Api_Models_Shared_Image | undefined {
  const mediaProps = getMediaProperties(media ?? undefined);

  if (!mediaProps?.url) {
    return undefined;
  }

  return {
    url: mediaProps.url,
    description: mediaProps.altText ?? null,
    title: mediaProps.altText ?? null,
  };
}

export function overlayWoongebouwListItem(
  wrmItem: Woonstad_Foundation_Portfolio_Api_Models_WRM_StadswonenComplex,
  cmsItem?: WoongebouwDetailPaginaContentResponseModel,
): Woonstad_Foundation_Portfolio_Api_Models_WRM_StadswonenComplex {
  if (!cmsItem?.properties) {
    return wrmItem;
  }

  const properties = cmsItem.properties;
  const cmsTitle = properties.titel ?? cmsItem.name;
  const cmsImage = mapCmsMediaToWrmImage(properties.afbeeldingenCarousel?.[0]);
  const cmsUrl = cmsItem.route?.path?.split('/').filter(Boolean).pop();

  const cmsDescription = properties.introductieTekst
    ? stripHTML(properties.introductieTekst)
    : undefined;

  return {
    ...wrmItem,
    title: cmsTitle ?? wrmItem.title,
    name: cmsTitle ?? wrmItem.name,
    image: cmsImage ?? wrmItem.image,
    description: cmsDescription ?? wrmItem.description,
    url: cmsUrl ?? wrmItem.url,
  };
}

export function overlayWoongebouwSearchResponse<
  T extends { items?: Array<Woonstad_Foundation_Portfolio_Api_Models_WRM_StadswonenComplex> | null },
>(
  wrmResponse: T,
  cmsByComplexNumber: Map<number, WoongebouwDetailPaginaContentResponseModel>,
): T {
  if (!wrmResponse.items?.length) {
    return wrmResponse;
  }

  return {
    ...wrmResponse,
    items: wrmResponse.items.map((item) => {
      const complexNumber = item.number;

      if (complexNumber === undefined) {
        return item;
      }

      return overlayWoongebouwListItem(item, cmsByComplexNumber.get(complexNumber));
    }),
  };
}

function normalizeComplexName(value?: string | null): string | undefined {
  const normalized = value?.trim().toLowerCase();

  return normalized || undefined;
}

export function countPublishedAdsByComplexName(
  ads: Array<{ complexName?: string | null }>,
): Map<string, number> {
  const counts = new Map<string, number>();

  for (const ad of ads) {
    const key = normalizeComplexName(ad.complexName);

    if (!key) {
      continue;
    }

    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return counts;
}

export function filterPublishedAdsForComplex<T extends { complexName?: string | null }>(
  ads: T[],
  complexNames: Array<string | null | undefined>,
): T[] {
  const keys = new Set(
    complexNames
      .map(normalizeComplexName)
      .filter((key): key is string => Boolean(key)),
  );

  if (keys.size === 0) {
    return [];
  }

  return ads.filter((ad) => {
    const key = normalizeComplexName(ad.complexName);

    return key !== undefined && keys.has(key);
  });
}

export function overlayPublishedAvailability<
  T extends { name?: string | null; title?: string | null; available?: number },
>(
  complexes: T[],
  ads: Array<{ complexName?: string | null }>,
): T[] {
  const counts = countPublishedAdsByComplexName(ads);

  return complexes.map((complex) => {
    const key = normalizeComplexName(complex.name) ?? normalizeComplexName(complex.title);

    return {
      ...complex,
      available: key ? (counts.get(key) ?? 0) : 0,
    };
  });
}
